/* eslint-disable css-modules/no-unused-class */
import { useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import Tooltip from 'jl-design-system/components/tooltip/Tooltip';
import { UnderlinedButton } from 'jl-design-system/components/button/Button';

// Types
import { OrderItemProps } from './OrderItem.types';

// Config
import orderItemState from './OrderItem.state';
import {
  getInformationList,
  getPromotionalText,
  getReducedQuantityText,
  getReducedQuantityTextForFabricItem,
  getSubtotalText,
} from './OrderItemHelper';
import { shouldShowCollectionHelpText } from '@utils/collection/collectionHelp';
import { isEmptyObject } from '@utils/object';
import { getItemUndeliverableReason } from '@utils/orderform/getItemUndeliverableReason';
import { shouldShowPersonalisationHelpText } from '@utils/personalisation/personalisationHelp';

// Components
import Brand from '@components/brand';
import Container from '@components/container';
import BodyText from '@components/body-text';
import OrderItem from './OrderItem';
import ProductImageWithMagnifier from '@components/product-image-with-magnifier';
import ItemRestrictionMessage from '@components/undeliverable-error-message/ItemRestrictionMessage';

// Styles
import styles from './order-item.scss';

const SinglePageOrderItemContent = ({
  availability = {},
  basketView = false,
  batchedView = false,
  brand,
  brandHeadingTag = 'h3',
  displayImage,
  hideOutOfStockText = false,
  hideQuantities = false,
  hideSubtotals = false,
  id = '',
  imageUrl = '',
  information,
  isOrderConfirmation = false,
  isPosCreditItem = false,
  modal = false,
  name = '',
  partnerDiscountRate,
  personalised = false,
  priceReductionHistory,
  promotionalMessageTitle,
  quantity,
  reducedQuantity,
  removeItem,
  sidebar = false,
  subtotal = '',
  subtotalDiscounted,
  type,
  uom = '',
}: OrderItemProps) => {
  const { orderComplete, deliveries, undeliverableItemsInBasket } = useSelector(orderItemState);
  const isUndeliverableItem = getItemUndeliverableReason(undeliverableItemsInBasket ?? [], id);
  const showUndeliverableMessage = isUndeliverableItem?.reasons?.length > 0;
  const showCollectionHelp = shouldShowCollectionHelpText({ deliveries, isChildItem: false, id, orderComplete });
  const showPersonalisationMessage = shouldShowPersonalisationHelpText({
    isChildItem: false,
    orderComplete,
    personalised,
  });
  const isItemFabric = uom !== '' && !type?.startsWith('m2m');
  const quantityUnit = isItemFabric ? ` ${quantity === 1 ? 'metre' : 'metres'}` : '';
  const showPriceReductionHistory =
    (sidebar || basketView || isOrderConfirmation) &&
    !hideSubtotals &&
    !batchedView &&
    !!(promotionalMessageTitle || partnerDiscountRate);
  const showPartnerDiscountRate = !!(partnerDiscountRate && subtotalDiscounted);
  const showPromotionalMessageTitle =
    !!(partnerDiscountRate && subtotalDiscounted) || !isEmptyObject(priceReductionHistory);

  const brandContent = <Brand brand={brand} headingTag={brandHeadingTag} />;

  const nameContent = (
    <BodyText marginBottom="2" tag="p" testId="order-item-name">
      {name}
    </BodyText>
  );

  const itemMetaContent = (
    <div className={styles.itemType}>
      {showCollectionHelp && (
        <BodyText marginBottom="2">
          <strong>Collection unavailable</strong>
        </BodyText>
      )}
      <Container marginBottom="2" renderIf={showPersonalisationMessage}>
        <Tooltip
          anchor={sidebar ? 'right' : 'left'}
          buttonLabel="Includes personalisation"
          icon
          id={`personalisationTooltip-${id}`}
        >
          <BodyText marginBottom="2">
            Once you’ve placed your order, please email your personalisation details to the supplier.
          </BodyText>
          <BodyText>
            Details on how to do this can be found on the product details page or the order confirmation page.
          </BodyText>
        </Tooltip>
      </Container>
      {availability.preOrder && <BodyText marginBottom="2">Pre-order</BodyText>}
      {information && !batchedView && Array.isArray(information) && getInformationList(information, styles)}
    </div>
  );

  const subtotalContent =
    !hideSubtotals &&
    !batchedView &&
    getSubtotalText({
      brand,
      sidebar,
      subtotal,
      subtotalDiscounted,
      styles,
      basketView: false,
      isChildItem: false,
      isOrderConfirmation,
      priceReductionHistory,
      promotionalMessageTitle,
      partnerDiscountRate,
    });

  const quantityContent = reducedQuantity ? (
    <div className={styles.reducedQuantityContainer}>
      {isItemFabric
        ? getReducedQuantityTextForFabricItem(quantity, reducedQuantity, styles)
        : getReducedQuantityText(quantity, reducedQuantity, styles)}
    </div>
  ) : (
    !hideQuantities && (
      <p
        aria-label={`Quantity: ${quantity}${quantityUnit}`}
        className={styles.singlePageQuantity}
        data-testid="order-item-quantity"
      >
        {`Qty: ${quantity}${quantityUnit}`}
      </p>
    )
  );

  const outOfStockContent = !hideOutOfStockText && Number(availability?.stockLevel) === 0 && (
    <p className={styles.outOfStock}>
      Sorry, this item is no longer available. Please <UnderlinedButton labelText="remove" onClick={removeItem} /> this
      item to continue.
    </p>
  );

  return (
    <Container
      background="white"
      className={cx(styles.container, styles.singlePageCard, {
        [styles.basketView]: basketView,
        [styles.batchedView]: batchedView,
        [styles.posCredit]: isPosCreditItem,
        [styles.orderConfirmation]: isOrderConfirmation,
        [styles.singlePageSidebar]: sidebar,
        [styles.singlePageModal]: modal,
      })}
      testId="items"
    >
      <div className={styles.wrapper}>
        <div className={styles.singlePageMedia}>
          {displayImage && (
            <ProductImageWithMagnifier
              imageUrl={imageUrl}
              isSinglePageCheckout
              name={name}
              showMagnifyingIcon={sidebar}
            />
          )}
        </div>

        <div
          className={cx(styles.itemDetailsContainer, styles.singlePageCardDetails)}
          data-testid="item-details-container"
        >
          <div className={styles.singlePageMainColumn}>
            <div className={styles.singlePageTopRow}>
              <div className={styles.singlePageBrand}>{brandContent}</div>
              <div className={styles.singlePageValueColumn}>{subtotalContent}</div>
            </div>
            <div className={styles.singlePageNameRow}>{nameContent}</div>
            <div className={styles.singlePageQuantityRow}>{quantityContent}</div>
            {itemMetaContent}
            {outOfStockContent}
          </div>
        </div>
      </div>
      <Container renderIf={showPriceReductionHistory} textAlign="right">
        {getPromotionalText({
          brand,
          showPartnerDiscountRate,
          showPromotionalMessageTitle,
          styles,
          promotionalMessageTitle,
          partnerDiscountRate,
        })}
      </Container>
      {showUndeliverableMessage && (
        <Container marginTop="2">
          <ItemRestrictionMessage undeliverableReasons={isUndeliverableItem?.reasons} />
        </Container>
      )}
    </Container>
  );
};

const SinglePageOrderItem = (props: OrderItemProps) =>
  props.child || (!props.sidebar && !props.modal) ? (
    <OrderItem {...props} background="white" />
  ) : (
    <SinglePageOrderItemContent {...props} />
  );

export default SinglePageOrderItem;
